import { ApiError, req } from "../api.ts";
import { esc, h } from "../dom.ts";
import { navigate, type Params } from "../router.ts";
import { topNav } from "../ui/topnav.ts";
import { onDisconnect } from "../ui/lifecycle.ts";
import { copyText } from "../ui/clipboard.ts";
import { confirmDialog } from "../ui/confirm.ts";
import { Chessground } from "chessground";
import { commandId, ensurePlayer, PlayConnection, PlayJournal, readPlayer, outcomeMessage, sessionRecovery, type Slot } from "../play/client.ts";
import { acceptLiveOffer, LiveOfferConsent, takeLiveOffer } from "../play/offerConsent.ts";

export function playPage(content: string, active: "play" | "players" | "games" = "play"): HTMLElement {
  const root = h(`<div class="club-page"><main class="club-content">${content}</main></div>`);
  root.prepend(topNav(active)); return root;
}
const failure = (error: unknown) => error instanceof Error ? error.message : "Could not connect. Please retry.";
const lastSlot = "chessme.last-slot.v1";
const invitationKey = (token: string) => `chessme.invite.v1:${token}`;
function rememberInvitation(token: string, slot: string): void {
  sessionStorage.setItem(invitationKey(token), slot);
  // Messages may reopen the link in a new Safari tab. This is a navigation hint;
  // GET /slots still requires the same participant's browser cookie.
  try { localStorage.setItem(invitationKey(token), slot); } catch { /* this tab can still return */ }
}
function remember(slot: Slot): void {
  sessionStorage.setItem(lastSlot, slot.id);
  // A normal browser may open a new tab when returning from the native app.
  // This is only a navigation hint; the server still checks the current SID.
  try { localStorage.setItem(lastSlot, slot.id); } catch { /* the current tab can still return */ }
  if (slot.invite_url) rememberInvitation(slot.invite_url.split("/").at(-1)!, slot.id);
}
function mounted(root: HTMLElement, run: () => () => void): void {
  requestAnimationFrame(() => { if (root.isConnected) onDisconnect(root,run()); });
}
function review(root: HTMLElement, setup: Slot["setup"], color: "white" | "black"): () => void {
  const fen = setup?.transfer?.snapshot.final_fen;
  const holder = root.querySelector<HTMLElement>(".position-preview")!;
  if (!fen) { holder.hidden = true; holder.replaceChildren(); return () => {}; }
  holder.hidden = false;
  const board = Chessground(holder,{fen,orientation:color,viewOnly:true,coordinates:true});
  return () => board.destroy();
}

export async function playScreen(): Promise<HTMLElement> {
  let prior: string | null = null;
  try { prior = sessionStorage.getItem(lastSlot) ?? localStorage.getItem(lastSlot); } catch { /* explicit action will explain storage needs */ }
  if (prior && /^[A-Za-z0-9_-]{16,64}$/.test(prior)) {
    try {
      // Cookies may have been cleared or switched since this hint was stored.
      // Never bootstrap a guest, expose another player's slot, or create a new
      // invitation merely because Play was opened in a fresh tab.
      await req<Slot>("GET",`/api/play/slots/${prior}`);
    } catch { prior = null; }
  }
  const root = playPage(`<p class="eyebrow">A small chess club</p><h1>A game with someone.</h1>
    <p class="club-intro">Invite a friend or meet a player here. Open a board, take your time.</p>
    <section class="play-choices"><div class="club-panel"><h2>Invite a friend</h2><p>A private link, ready to share with any phone or computer.</p>
      <label class="field"><span>Your side</span><select name="color"><option value="white">White</option><option value="black">Black</option></select></label>
      <button class="btn primary" data-create>Create invitation</button><p class="fine">Standard chess · no clock · unrated</p></div>
    <div class="club-panel"><h2>Find a player</h2><p>Join the people who are here and ready for a game.</p><a class="btn" data-link href="/club">Find a player</a></div></section>
    <p class="msg" role="status" aria-live="polite"></p>
    ${prior && /^[A-Za-z0-9_-]{16,64}$/.test(prior) ? `<p><a data-link href="/play/slot/${esc(prior)}">Return to your last invitation</a></p>` : ""}
    <p class="fine">No account needed to play. Save your games with an email whenever you choose.</p>`);
  const button = root.querySelector<HTMLButtonElement>("[data-create]")!;
  button.onclick = async () => {
    button.disabled = true;
    const message = root.querySelector<HTMLElement>(".msg")!; message.textContent = "Preparing your invitation…";
    try {
      const player = await ensurePlayer();
      const color = root.querySelector<HTMLSelectElement>("select")!.value;
      const result = await new PlayJournal(player.id,"create-slot").send("/api/play/slots",{actor_id:player.id,command_id:commandId(),setup:{kind:"fresh",color,clock_mode:"untimed",rated:false}}, r => { if (r.slot) remember(r.slot); });
      if (result.outcome === "rejected" || !result.slot) throw new Error(outcomeMessage(result.code));
      navigate(`/play/slot/${result.slot.id}`);
    } catch (error) { message.textContent = failure(error); button.textContent = "Retry invitation"; sessionRecovery(root,error); }
    finally { button.disabled = false; }
  };
  return root;
}

interface Preview { id:string; revision:number; state:string; host_name:string; host_color:"white"|"black"; setup:Slot["setup"]; expires_at:number; kind:string; snapshot_digest:string }
export async function playInvitationScreen(params: Params): Promise<HTMLElement> {
  const token = params.token;
  try {
    const known = sessionStorage.getItem(invitationKey(token)) ?? localStorage.getItem(invitationKey(token));
    if (known && /^[A-Za-z0-9_-]{16,64}$/.test(known)) {
      await req<Slot>("GET",`/api/play/slots/${known}`); navigate(`/play/slot/${known}`);
      return playPage("<p>Opening your invitation…</p>");
    }
  } catch { /* A remembered preview without a claim has no participant authority. */ }
  let preview: Preview;
  try { preview = await req<Preview>("GET",`/api/play/invitations/${encodeURIComponent(token)}`); }
  catch (error) {
    return playPage(`<h1>Invitation unavailable</h1><p>${esc(failure(error))}</p><p>If you already joined, use the original browser or recover your saved games.</p><a class="btn" data-link href="/my-games">My games</a><a class="btn ghost" data-link href="/">Create a new invitation</a>`);
  }
  const color = preview.host_color === "white" ? "black" : "white";
  if (preview.state === "waiting_for_host") {
    const root = playPage(`<h1>Continue our game live?</h1><p>You keep ${color}. Same position · no clock · unrated.</p>
      <div class="position-preview cg-board-wrap"></div><p class="msg" role="status">Waiting for your friend to open the live board…</p>
      <p>You can keep playing in Messages instead. A new move ends this offer.</p><p class="fine">Offer expires ${esc(new Date(preview.expires_at*1000).toLocaleString())}.</p>`);
    root.classList.add("live-offer");
    mounted(root, () => {
      const destroy = review(root, preview.setup, color);
      let stopped = false, timer: ReturnType<typeof setTimeout>;
      const refresh = async () => {
        if (stopped) return;
        try {
          if (document.visibilityState === "visible") {
            const current = await req<Preview>("GET", `/api/play/invitations/${encodeURIComponent(token)}`);
            if (stopped) return;
            if (current.state !== "waiting_for_host") { navigate(`/play/invite/${token}`); return; }
          }
        } catch (error) {
          if (stopped) return;
          root.querySelector(".msg")!.textContent = failure(error);
          if (error instanceof ApiError && [404,409].includes(error.status)) return;
        }
        timer = setTimeout(() => void refresh(), 2000);
      };
      timer = setTimeout(() => void refresh(), 1500);
      return () => { stopped = true; clearTimeout(timer); destroy(); };
    });
    return root;
  }
  const root = playPage(`<h1>${preview.kind === "import" ? "Continue our game live?" : `${esc(preview.host_name)} invited you to play.`}</h1><p>${preview.kind === "import" ? `An offer from ${esc(preview.host_name)}. You keep ${color}.` : `You will play ${color}.`} Standard chess · no clock · unrated.</p>
    <div class="position-preview cg-board-wrap" hidden></div>
    ${preview.kind === "import" ? "<p>Accept this position and keep this page open. Your friend starts the live board when you are both here.</p>" : "<p>Join when you are ready. The game starts when both browsers are connected and ready.</p>"}
    <button class="btn primary" data-join>${preview.kind === "import" ? "Accept live offer" : "Join game"}</button><p class="msg" role="status" aria-live="polite"></p><p class="fine">No sign-in or app needed. Invitation expires ${esc(new Date(preview.expires_at*1000).toLocaleString())}.</p>`);
  if (preview.kind === "import") root.classList.add("live-offer");
  mounted(root,() => review(root,preview.setup,color));
  const button = root.querySelector<HTMLButtonElement>("[data-join]")!;
  button.onclick = async () => {
    button.disabled = true;
    let stayedVisible = document.visibilityState === "visible";
    const visibility = () => { if (document.visibilityState !== "visible") stayedVisible = false; };
    document.addEventListener("visibilitychange", visibility);
    try {
      const player = await ensurePlayer();
      rememberInvitation(token, preview.id);
      const result = await new PlayJournal(player.id,`claim:${preview.id}`).send(`/api/play/slots/${preview.id}/commands`,{
        actor_id:player.id,command_id:commandId(),expected_revision:preview.revision,action:{type:"claim",invite_token:token},
      },r => { if (r.slot) remember(r.slot); });
      if (result.code === "self_claim" && result.slot) { navigate(`/play/slot/${result.slot.id}`); return; }
      if (result.outcome === "rejected" || !result.slot) throw new Error(outcomeMessage(result.code));
      if (!root.isConnected) return;
      if (preview.kind === "import" && stayedVisible && result.slot.revision === preview.revision + 1
        && result.slot.snapshot_digest === preview.snapshot_digest
        && (color === "white" ? result.slot.white_id : result.slot.black_id) === player.id) acceptLiveOffer(result.slot);
      navigate(`/play/slot/${result.slot.id}`);
    } catch (error) { root.querySelector(".msg")!.textContent = failure(error); button.disabled = false; sessionRecovery(root,error); }
    finally { document.removeEventListener("visibilitychange", visibility); }
  };
  return root;
}

export async function playSlotScreen(params: Params): Promise<HTMLElement> {
  // Read only on return: a missing old session must never become a replacement seat.
  if (!await readPlayer()) return playPage(`<h1>Return to your game</h1><p>This browser no longer has the private session for this invitation. Open it in the original browser, or recover games you saved.</p><a class="btn" data-link href="/my-games">My games</a><a class="btn ghost" data-link href="/">Start a new game</a>`);
  let slot: Slot;
  try { slot = await req<Slot>("GET",`/api/play/slots/${encodeURIComponent(params.id)}`); }
  catch (error) { const root = playPage(`<h1>Invitation unavailable</h1><p>${esc(failure(error))}</p><a class="btn" data-link href="/my-games">My games</a><a data-link href="/">Start a new game</a>`); sessionRecovery(root,error); return root; }
  remember(slot);
  if (slot.game_id) { navigate(`/game/${slot.game_id}`); return playPage("<p>Opening your game…</p>"); }
  const root = playPage(`<h1 data-title>Waiting for your friend</h1><p data-people></p><p data-side></p><p>Standard chess · no clock · unrated</p>
    <div class="position-preview cg-board-wrap" hidden></div><p data-deadline class="fine"></p>
    <div class="invitation-share" hidden><label class="field"><span>Invitation link</span><input readonly aria-label="Invitation link" /></label>
      <a class="btn primary" data-native-share hidden>Send live offer</a>
      <button class="btn" data-share>Share invitation</button><button class="btn ghost" data-copy>Copy link</button></div>
    <p data-connection class="fine" role="status"></p><p data-readiness></p>
    <div class="club-actions"><button class="btn primary" data-ready>I’m ready</button><button class="btn primary" data-approve hidden>Confirm this player and position</button><button class="btn ghost" data-withdraw hidden>Wait a moment</button><button class="btn ghost" data-cancel>Cancel invitation</button></div>
    <p class="msg" role="status" aria-live="polite"></p><a data-link href="/">Back to Play</a>`);
  let intent: number | null = null; let busy = false; let loading = false; let dirty = false; let dead = false; let stopped = false;
  let consent = takeLiveOffer(slot);
  let lastConnection: string | null = null;
  let destroyReview = () => {};
  const message = root.querySelector<HTMLElement>(".msg")!;
  const journal = new PlayJournal(slot.player_id,`slot:${slot.id}`);
  const connection = new PlayConnection(() => {
    if (lastConnection && connection.id !== lastConnection) { intent = null; consent = null; }
    lastConnection = connection.id;
    render(); void refresh();
  }, () => void refresh());
  const isHost = () => slot.player_id === slot.host.id;
  const side = (): "white" | "black" => slot.white_id ? (slot.player_id === slot.white_id ? "white" : "black") : slot.setup?.color ?? slot.setup?.transfer?.source.initiator_color ?? "white";
  const ownReady = () => isHost() ? slot.readiness.host : slot.readiness.recipient;
  function render() {
    const expired = slot.state === "expired" || slot.state === "cancelled";
    const host = isHost();
    const offeredInMessages = slot.setup?.transfer?.exporter?.build === "ios-messages-live-offer-v1";
    root.classList.toggle("live-offer", slot.kind === "import");
    root.querySelector("[data-title]")!.textContent = expired ? (slot.state === "expired" ? "Invitation expired" : "Invitation cancelled") : slot.recipient ? (slot.kind === "import" ? "Continue together" : "Your board is ready") : "Waiting for your friend";
    root.querySelector("[data-people]")!.textContent = `${slot.host.name}${slot.recipient ? ` · ${slot.recipient.name}` : " · Your friend has not joined yet"}`;
    root.querySelector("[data-side]")!.textContent = `You play ${side()}.`;
    root.querySelector("[data-deadline]")!.textContent = `Expires ${new Date(slot.expires_at*1000).toLocaleString()}`;
    root.querySelector<HTMLElement>("[data-deadline]")!.hidden = slot.kind === "import" && slot.recipient !== null;
    root.querySelector("[data-connection]")!.textContent = connection.error;
    root.querySelector("[data-readiness]")!.textContent = expired ? "Create a new invitation to play." : !slot.recipient ? "Share the link. Keep this page open when you are ready to play." : !slot.approved ? "Waiting for the host to confirm the player and position." : ownReady() ? "You’re ready. Waiting for the other player…" : "Check the players and sides, then mark yourself ready.";
    if (!expired && slot.kind === "import") {
      root.querySelector("[data-readiness]")!.textContent = !slot.recipient ? (offeredInMessages ? "Your friend can accept the offer in your Messages game. Keep this board open." : "Send your friend the live offer, then return here to play.")
        : !slot.approved && host ? `${slot.recipient.name} joined your offer. Check that this is your friend, then start live.`
        : consent || ownReady() ? `You accepted this position. Keep this page open${!slot.approved ? ` while ${slot.host.name} starts the board` : " while your friend connects"}.`
        : !slot.approved ? "Your friend will start the board. Stay here and select I’m ready when they do." : "Ready to continue from this position?";
    }
    const share = root.querySelector<HTMLElement>(".invitation-share")!; share.hidden = !slot.invite_url || expired || (slot.kind === "import" && (slot.recipient !== null || offeredInMessages));
    if (slot.invite_url) share.querySelector("input")!.value = slot.invite_url;
    const nativeShare = root.querySelector<HTMLAnchorElement>("[data-native-share]")!;
    const source = slot.setup?.transfer?.source.match_id;
    const appleMobile = /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    nativeShare.hidden = !host || slot.kind !== "import" || !appleMobile || !source || !/^[a-f0-9]{8}(-[a-f0-9]{4}){3}-[a-f0-9]{12}$/i.test(source);
    if (!nativeShare.hidden) nativeShare.href = `si.resonanca.hard.chess.api://share-live?id=${source}`;
    share.querySelector<HTMLElement>("label")!.hidden = !nativeShare.hidden;
    share.querySelector<HTMLButtonElement>("[data-share]")!.hidden = !navigator.share || !nativeShare.hidden;
    const ready = root.querySelector<HTMLButtonElement>("[data-ready]")!;
    ready.hidden = expired || !slot.approved || ownReady() || consent !== null; ready.disabled = busy || stopped || !slot.recipient || !connection.id;
    const approve = root.querySelector<HTMLButtonElement>("[data-approve]")!;
    approve.hidden = expired || !host || slot.approved || !slot.recipient; approve.disabled = busy || stopped || !connection.id;
    approve.textContent = slot.kind === "import" ? `Start live with ${slot.recipient?.name ?? "your friend"}` : "Confirm this player and position";
    const cancel = root.querySelector<HTMLButtonElement>("[data-cancel]")!; cancel.hidden = expired || !host; cancel.disabled = busy || stopped;
    const withdraw = root.querySelector<HTMLButtonElement>("[data-withdraw]")!; withdraw.hidden = expired || (!ownReady() && intent === null && consent === null); withdraw.disabled = busy || stopped;
  }
  async function refresh() {
    if (dead || stopped) return;
    if (loading) { dirty = true; return; }
    loading = true;
    try {
      const next = await req<Slot>("GET",`/api/play/slots/${slot.id}`);
      if (dead) return;
      if (next.revision !== slot.revision) intent = null;
      if (consent && !consent.accepts(next)) consent = null;
      const changedPosition = next.snapshot_digest !== slot.snapshot_digest;
      slot = next;
      if (changedPosition) { destroyReview(); destroyReview = review(root,slot.setup,side()); }
      if (slot.game_id) { navigate(`/game/${slot.game_id}`); return; }
      render();
      if (!busy && (intent === slot.revision || consent !== null) && document.visibilityState === "visible" && connection.id && !ownReady() && slot.approved && slot.state === "paired") {
        consent = null; void ready();
      }
    } catch (error) { if (!dead) {
      message.textContent = failure(error); sessionRecovery(root,error);
      if (error instanceof ApiError && [401,403,404].includes(error.status)) {
        stopped = true; intent = null; consent = null; connection.close();
        root.querySelectorAll<HTMLButtonElement>('.club-actions button').forEach(button => button.disabled = true);
      }
    } }
    finally { loading = false; if (dirty && !dead) { dirty = false; void refresh(); } }
  }
  async function act(action: Record<string,unknown>) {
    if (busy || dead || stopped) return;
    busy = true; render(); message.textContent = "";
    try {
      const result = await journal.send(`/api/play/slots/${slot.id}/commands`,{actor_id:slot.player_id,command_id:commandId(),expected_revision:slot.revision,action});
      if (result.outcome === "rejected") { message.textContent = outcomeMessage(result.code); intent = null; consent = null; }
    } catch (error) { message.textContent = `${failure(error)} Your previous action will be checked on retry.`; intent = null; consent = null; sessionRecovery(root,error); }
    finally { busy = false; render(); void refresh(); }
  }
  async function ready() {
    if (!connection.id || !slot.white_id || !slot.black_id) return;
    intent = slot.revision;
    await act({type:"ready",connection_id:connection.id,digest:slot.snapshot_digest,white_id:slot.white_id,black_id:slot.black_id});
  }
  root.querySelector<HTMLButtonElement>("[data-ready]")!.onclick = () => void ready();
  root.querySelector<HTMLButtonElement>("[data-approve]")!.onclick = () => {
    if (slot.kind === "import") consent = new LiveOfferConsent(slot);
    void act({type:"approve",recipient_id:slot.recipient?.id});
  };
  root.querySelector<HTMLButtonElement>("[data-cancel]")!.onclick = async () => {
    if (await confirmDialog("Cancel this invitation? Your friend will need a new link.", {danger:true})) {
      intent = null; consent = null; void act({type:"cancel"});
    }
  };
  root.querySelector<HTMLButtonElement>("[data-withdraw]")!.onclick = () => { intent = null; consent = null; void act({type:"withdraw_ready"}); };
  root.querySelector<HTMLButtonElement>("[data-copy]")!.onclick = async () => {
    try { await copyText(slot.invite_url!); message.textContent = "Invitation copied."; }
    catch { message.textContent = "Select and copy the invitation link above."; }
  };
  const share = root.querySelector<HTMLButtonElement>("[data-share]")!; share.hidden = !navigator.share;
  share.onclick = async () => { try { await navigator.share({title:"A game of chess",url:slot.invite_url}); } catch (error) { if (!(error instanceof DOMException && error.name === "AbortError")) message.textContent = "Use Copy link to share this invitation."; } };
  render();
  mounted(root,() => {
    if (document.visibilityState !== "visible") { consent = null; intent = null; }
    destroyReview = review(root,slot.setup,side());
    connection.start(); const timer = window.setInterval(() => { if (document.visibilityState === 'visible') void refresh(); },10000);
    const visibility = () => {
      if (document.visibilityState !== 'visible') { intent = null; consent = null; message.textContent = 'Select I’m ready again when you return to play.'; }
      else void refresh();
    };
    document.addEventListener('visibilitychange', visibility);
    return () => { dead = true; clearInterval(timer); document.removeEventListener("visibilitychange", visibility); connection.close(); destroyReview(); };
  });
  return root;
}
